/**
 * TaxModal (m-tax) — Edit tax rate and settings.
 *
 * Spec: rate + as-of date, prefilled from job-address jurisdiction; tax-exempt toggle.
 */

import React, { useState, useEffect } from 'react';
import Slideover from '../../../ui/Slideover';
import { Button } from '../../../ui/Button';
import { Input } from '../../../ui/Input';
import { DatePicker } from '../../../ui/DatePicker';
import SelectMenu from '../../../ui/SelectMenu';
import { settingsAPI } from '../../../../services/api';
import { taxRowDecimal, formatTaxPct } from '../../../../quote-builder/resolveLoadedTaxRate';
import { dealerToday } from '../../../../utils/dealerDate';

// IDI-668 (D5): `rows` feeds the "Saved rate" dropdown; `keepRate` says the
// current rate is the user's own, so the 0% auto-fill must not replace it.
export default function TaxModal({ open, onClose, taxRate, taxExempt, jurisdiction, onSave, rows = [], keepRate = false }) {
  const [rate, setRate] = useState('');
  const [exempt, setExempt] = useState(false);
  const [asOfDate, setAsOfDate] = useState('');

  useEffect(() => {
    setRate(taxRate ? String((taxRate * 100).toFixed(3)) : '');
    setExempt(!!taxExempt);
    setAsOfDate(dealerToday()); // IDI-615 D2
  }, [taxRate, taxExempt, open]);

  // Tax resolution cascade (T2/T3): auto-lookup rate from Settings when
  // the modal opens with a jurisdiction. Matches the state against the
  // tenant's configured tax rows. Falls back to the default-flagged row
  // (T3) if no state match. Falls back to 0 (T4) if no rows at all.
  useEffect(() => {
    if (!open || taxRate || keepRate) return; // don't override an existing (or own 0) rate
    let cancelled = false;
    (async () => {
      try {
        const rates = await settingsAPI.getTaxRates();
        const arr = Array.isArray(rates) ? rates : rates?.rates || rates?.data || [];
        if (cancelled || !arr.length) return;
        // T2: match by state
        const stateMatch = jurisdiction
          ? arr?.find(r => (r.state || '').toUpperCase() === jurisdiction.toUpperCase())
          : null;
        // T3: default row (or first row if no default flag)
        const defaultRow = arr?.find(r => r.isDefault || r.is_default) || arr[0];
        const matched = stateMatch || defaultRow;
        if (matched && !cancelled) {
          // IDI-697: rows store a percent (6.875 means 6.875%), 1 or less too.
          const resolvedRate = Number(matched.rate ?? matched.tax_rate ?? 0) || 0;
          setRate(resolvedRate.toFixed(3));
        }
      } catch { /* Settings unavailable — leave rate empty, user sets manually */ }
    })();
    return () => { cancelled = true; };
  }, [open, jurisdiction, taxRate, keepRate]);

  const handleSave = () => {
    onSave({
      taxRate: exempt ? 0 : parseFloat(rate || 0) / 100,
      taxExempt: exempt,
      taxAsOfDate: asOfDate,
    });
    onClose();
  };

  const footer = (
    <div className="flex gap-2 justify-end w-full">
      <Button variant="secondary" onClick={onClose}>Cancel</Button>
      <Button onClick={handleSave}>Save Tax Settings</Button>
    </div>
  );

  return (
    <Slideover open={open} onClose={onClose} title="Edit Tax" subtitle="Tax rate for this quote" footer={footer} width="sm">
      <div className="space-y-4">
        {/* Jurisdiction info */}
        {jurisdiction && (
          <div className="card p-3 text-xs" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--neutral-50)' }}>
            <p style={{ color: 'var(--color-text-secondary)' }}>
              Prefilled from job address jurisdiction: <strong>{jurisdiction}</strong>
            </p>
          </div>
        )}

        {/* Tax-exempt toggle */}
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={exempt}
            onChange={(e) => setExempt(e.target.checked)}
            className="rounded border-[color:var(--color-border)] text-red-600 focus:ring-red-500"
              style={{ accentColor: 'var(--color-action-primary)' }}
            />
          <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>Tax Exempt</span>
        </label>

        {/* Tax rate */}
        {!exempt && (
          <>
            {/* IDI-380: when no rate is set and the quote isn't exempt, say so
                explicitly instead of implying a 0% exemption. */}
            {(rate === '' || Number(rate) === 0) && (
              <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                No tax rate set for this quote.
              </p>
            )}
            {rows.length > 0 && (
              <div>
                <label className="label" htmlFor="tax-saved-rate">Saved rate</label>
                <SelectMenu
                  id="tax-saved-rate"
                  aria-label="Saved rate"
                  width="full"
                  placeholder="Choose a saved rate"
                  value={rows.findIndex((r) => rate !== '' && Math.abs(taxRowDecimal(r) * 100 - Number(rate)) < 1e-6)}
                  onChange={(i) => { if (rows[i]) setRate((taxRowDecimal(rows[i]) * 100).toFixed(3)); }}
                  options={rows.map((r, i) => ({
                    value: i,
                    label: [r.name || 'Tax rate', r.state ? String(r.state).toUpperCase() : null, `${formatTaxPct(taxRowDecimal(r))}%`].filter(Boolean).join(' · '),
                  }))}
                />
              </div>
            )}
            <Input
              label="Tax Rate (%)"
              type="number"
              min="0"
              max="25"
              step="0.01"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder="0.000"
            />
            <DatePicker
              label="As of Date"
              value={asOfDate}
              onChange={(val) => setAsOfDate(val)}
            />
          </>
        )}

        {/* Preview */}
        {!exempt && rate && (
          <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            Tax rate: {parseFloat(rate).toFixed(3)}% effective {asOfDate}
          </p>
        )}
      </div>
    </Slideover>
  );
}
